import FilterAlt from "@mui/icons-material/FilterAlt";
import {
	Box,
	CircularProgress,
	FormControlLabel,
	ListSubheader,
	MenuItem,
	Radio,
	RadioGroup,
	Stack,
	Typography
} from "@mui/material";
import axios from "axios";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import AddConditionButton from "../buttons/AddConditionButton";
import RaisedTrashIconButton from "../buttons/RaisedTrashIconButton";
import type { Option } from "../constants";
import ComparativeOperatorsSelect from "../fields/ComparativeOperatorsSelect";
import type { DatabaseTableOptionV2, ResourceCategoryV2, VariableGroupV2, VisibleGroupKey } from "../fields/form-entity-select";
import FormEntitySelect from "../fields/form-entity-select";
import LogicField from "../fields/LogicField";
import type { RuleVariable } from "../hooks/useRuleVariables";
import { type OperatorOption, type SortOrderOption, type TableFieldOption, type TableOption, type TableSource } from "../types/configs";
import { getAssignmentValueFormEntityBinding } from "../utils/assignment-statement-display-utils";
import { buildFormulaVariableRef, buildScreenFieldRef, selectedFieldMetaFromFormulaVariable, selectedFieldMetaFromScreenField, type FormulaVariableRef, type ScreenFieldRef } from "../utils/data-field-type-utils";
import { inferValueCategoryFromEntityFields, isDecisionValueEntityRow } from "../utils/decision-statement-value-utils";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface TableGroup {
	label: string;
	icon?: React.ReactElement;
	countLabel?: string;
	options: TableOption[];
	source: TableSource;
}

export interface FilterCondition {
	id: string;
	field: string;
	databaseTableFieldId?: string;
	screenTableFieldId?: string;
	operator: string;
	/** Bound to FormEntitySelect; for literals mirrors plainTextValue. */
	value: string;
	plainTextValue?: string;
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueDatabaseTableFieldName?: string;
	valueScreenFieldName?: string;
	valueScreenField?: ScreenFieldRef;
	valueScreenTableField?: ScreenFieldRef;
	valueSimpleVariableName?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueFormulaVariableName?: string;
	valueFormulaVariable?: FormulaVariableRef;
	valueDatabaseSingleRowVariableId?: string;
	valueDatabaseSingleRowVariableName?: string;
	valueGlobalConstant?: string;
	valueGlobalConstantName?: string;
}

export interface FilterFormState {
	filterName: string;
	databaseTableId?: string;
	screenTableId?: string;
	databaseTableVariableId?: string;
	tableName: string;
	sortByDatabaseTableFieldId?: string;
	sortByScreenTableFieldId?: string;
	sortOrder: string;
	recordScope: "firstRecord" | "allRecords";
	filterLogicExpression: string;
	conditions: FilterCondition[];
}

export interface FilterFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: FilterFormState) => void;
	initialState?: Partial<FilterFormState>;
	isSaving?: boolean;
	platformId: string | number;
	comparativeOperators?: OperatorOption[];
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	singleRowDatabaseVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	globalConstantOptions?: Option[];
	suppressComparativeOperatorsAutoFetch?: boolean;
	getDuplicateNameError?: (name: string) => string | undefined;
	ruleSimpleVariables?: RuleVariable[];
	ruleFormulaVariables?: RuleVariable[];
	ruleDatabaseSingleRowVariables?: RuleVariable[];
	/** Multi-row database table variables from rule state (only source for "Table Variable"). */
	ruleDatabaseMultiRowVariables?: RuleVariable[];
}

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_VARIABLE_GROUPS: VariableGroupV2[] = [];

const DEFAULT_SORT_ORDER_OPTIONS: SortOrderOption[] = [
	{ label: "Ascending (default)", value: "asc" },
	{ label: "Descending", value: "desc" }
];

const FilterForm = (props: FilterFormProps) => {
	const {
		open, onClose, onSave, initialState, isSaving = false, platformId, comparativeOperators,
		resourceVariableGroups = EMPTY_VARIABLE_GROUPS,
		formulaVariableOptions = EMPTY_OPTIONS,
		singleRowDatabaseVariableOptions = EMPTY_OPTIONS,
		resourceScreenFields = EMPTY_OPTIONS,
		globalConstantOptions = EMPTY_OPTIONS,
		suppressComparativeOperatorsAutoFetch = false,
		getDuplicateNameError,
		ruleSimpleVariables = [],
		ruleFormulaVariables = [],
		ruleDatabaseSingleRowVariables = [],
		ruleDatabaseMultiRowVariables = [],
	} = props;

	const authHeader = useAuthHeader();

	const tableVariableGroups = useMemo((): TableGroup[] => {
		if (!ruleDatabaseMultiRowVariables.length) return [];
		return [
			{
				label: "Database Table Variable - Multi Row",
				source: "databaseVariable",
				options: ruleDatabaseMultiRowVariables.map((v) => ({
					label: v.name,
					value: v.id,
					source: "databaseVariable" as const,
				})),
			},
		];
	}, [ruleDatabaseMultiRowVariables]);

	const [isLoadingTables, setIsLoadingTables] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [filterFieldOptions, setFilterFieldOptions] = useState<TableFieldOption[]>([]);
	const [fetchedScreenFields, setFetchedScreenFields] = useState<Option[]>([]);

	const mergedScreenFields = useMemo(
		() => (resourceScreenFields.length ? resourceScreenFields : fetchedScreenFields),
		[resourceScreenFields, fetchedScreenFields],
	);

	const resourceVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys: VisibleGroupKey[] = [];
		if (mergedScreenFields.length) keys.push("screenFields");
		const hasSimpleVariables = resourceVariableGroups.some((g) => g.options.length > 0);
		if (hasSimpleVariables) keys.push("simpleVariable");
		if (formulaVariableOptions.length) keys.push("formulaVariable");
		if (singleRowDatabaseVariableOptions.length) keys.push("singleRowDatabaseVariables");
		return keys;
	}, [
		mergedScreenFields,
		resourceVariableGroups,
		formulaVariableOptions.length,
		singleRowDatabaseVariableOptions.length,
	]);

	const valueFieldVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys = [...resourceVisibleGroups];
		if (globalConstantOptions.length) keys.push("globalConstants");
		return keys;
	}, [resourceVisibleGroups, globalConstantOptions.length]);

	const uniqueOperators = useMemo(
		() => Array.from(new Map((comparativeOperators ?? []).map((op) => [op.operator, op])).values()),
		[comparativeOperators]
	);

	const comparativeOperatorSelectOptions = useMemo(
		() => uniqueOperators.map((op) => ({ label: op.operator, value: op.operator })),
		[uniqueOperators],
	);

	const buildCondition = (overrides?: Partial<FilterCondition>): FilterCondition => ({
		id: overrides?.id ?? uuidv4(),
		field: overrides?.field ?? "",
		operator: overrides?.operator ?? "",
		value: overrides?.value ?? "",
		plainTextValue: overrides?.plainTextValue,
		valueDatabaseTableFieldId: overrides?.valueDatabaseTableFieldId,
		valueScreenFieldId: overrides?.valueScreenFieldId,
		valueDatabaseTableFieldName: overrides?.valueDatabaseTableFieldName,
		valueScreenFieldName: overrides?.valueScreenFieldName,
		valueScreenField: overrides?.valueScreenField,
		valueScreenTableField: overrides?.valueScreenTableField,
		valueSimpleVariableName: overrides?.valueSimpleVariableName,
		valueSimpleVariableId: overrides?.valueSimpleVariableId,
		valueFormulaVariableId: overrides?.valueFormulaVariableId,
		valueFormulaVariableName: overrides?.valueFormulaVariableName,
		valueFormulaVariable: overrides?.valueFormulaVariable,
		valueDatabaseSingleRowVariableId: overrides?.valueDatabaseSingleRowVariableId,
		valueDatabaseSingleRowVariableName: overrides?.valueDatabaseSingleRowVariableName,
		valueGlobalConstant: overrides?.valueGlobalConstant,
		valueGlobalConstantName: overrides?.valueGlobalConstantName,
	});

	type FilterFormInternalState = {
		filterName: string;
		table: string; // Internal state uses <source>-<tableId> format for the select
		tableName: string; // Store the label separately
		sortBy: string;
		sortOrder: string;
		recordScope: "firstRecord" | "allRecords";
		filterLogicExpression: string;
		conditions: FilterCondition[];
	};

	const [formState, setFormState] = useState<FilterFormInternalState>({
		filterName: "",
		table: "",
		tableName: "",
		sortBy: "",
		sortOrder: DEFAULT_SORT_ORDER_OPTIONS[0].value,
		recordScope: "firstRecord",
		filterLogicExpression: "1",
		conditions: [buildCondition()]
	});

	const sortOrderValue = useMemo(() => {
		const value = formState.sortOrder || DEFAULT_SORT_ORDER_OPTIONS[0].value;
		// Ensure the value matches one of the available options
		const isValidValue = DEFAULT_SORT_ORDER_OPTIONS.some(opt => opt.value === value);
		return isValidValue ? value : DEFAULT_SORT_ORDER_OPTIONS[0].value;
	}, [formState.sortOrder]);

	const resolveInitialTable = (): string => {
		const vid = initialState?.databaseTableVariableId;
		if (!vid) return "";
		if (!ruleDatabaseMultiRowVariables.some((v) => v.id === vid)) return "";
		return `databaseVariable-${vid}`;
	};

	const mapInitialConditions = (): FilterCondition[] =>
		initialState?.conditions?.length
			? initialState.conditions.map((c) => buildCondition({
				id: c.id,
				field: c.databaseTableFieldId ?? c.screenTableFieldId ?? c.field ?? "",
				operator: c.operator,
				value: c.value,
				plainTextValue: c.plainTextValue,
				valueDatabaseTableFieldId: c.valueDatabaseTableFieldId,
				valueScreenFieldId: c.valueScreenFieldId,
				valueDatabaseTableFieldName: c.valueDatabaseTableFieldName,
				valueScreenFieldName: c.valueScreenFieldName,
				valueScreenField: c.valueScreenField,
				valueScreenTableField: c.valueScreenTableField,
				valueSimpleVariableName: c.valueSimpleVariableName,
				valueSimpleVariableId: c.valueSimpleVariableId,
				valueFormulaVariableId: c.valueFormulaVariableId,
				valueFormulaVariableName: c.valueFormulaVariableName,
				valueFormulaVariable: c.valueFormulaVariable,
				valueDatabaseSingleRowVariableId: c.valueDatabaseSingleRowVariableId,
				valueDatabaseSingleRowVariableName: c.valueDatabaseSingleRowVariableName,
				valueGlobalConstant: c.valueGlobalConstant,
				valueGlobalConstantName: c.valueGlobalConstantName,
			}))
			: [buildCondition()];

	const getTableFieldOptions = useCallback(
		async (tableId: string, source: TableSource) => {

			if (!tableId) {
				setFilterFieldOptions([]);
				return [];
			}

			let resolvedDbTableId = tableId;
			if (source === "databaseVariable") {
				const variable = ruleDatabaseMultiRowVariables.find((v) => v.id === tableId);
				if (!variable?.source) {
					setFilterFieldOptions([]);
					return [];
				}
				resolvedDbTableId = variable.source;
			}
			try {
				const endpoint =
					source === "screen"
						? apiRoutes.getScreenTableFields(tableId)
						: apiRoutes.getDatabaseTableFields(resolvedDbTableId);
				const response = await axios.get(endpoint, { headers: authHeader });
				if (response.status !== 200) {
					throw new Error(`Failed to fetch table fields: ${response.statusText}`);
				}
				const data = response.data;

				const fields: TableFieldOption[] = data.map((field: any) => ({
					id: String(field.id ?? ""),
					name: field.name,
					value: String(field.id ?? ""),
					type: field.dataFieldType.name,
				}));

				setFilterFieldOptions(fields);
			} catch (error) {
				console.error("Error fetching table fields:", error);
				setFilterFieldOptions([]);
			}
		},
		[authHeader, ruleDatabaseMultiRowVariables],
	);

	const fetchTables = async () => {
		setIsLoadingTables(true);
		setLoadError(null);
		try {
			const response = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
			if (response.status !== 200) {
				throw new Error(`Failed to fetch tables: ${response.statusText}`);
			}
			const data = response.data;
			const rawGroups = data.tableGroups || [];

			const allScreenTables: { id: number; name: string }[] = [];
			for (const raw of rawGroups) {
				if (raw.screenTables?.length) allScreenTables.push(...raw.screenTables);
			}
			try {
				const screenFieldResults = await Promise.all(
					allScreenTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getScreenTableFields(table.id), { headers: authHeader });
							return (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: `${table.name}.${field.name}`,
								value: String(field.id),
							}));
						} catch {
							return [];
						}
					}),
				);
				setFetchedScreenFields(screenFieldResults.flat());
			} catch (resourceErr) {
				console.error("Error fetching filter value resource options:", resourceErr);
				setFetchedScreenFields([]);
			}

			const initialTable = resolveInitialTable();

			setFormState({
				filterName: initialState?.filterName ?? "",
				table: initialTable,
				tableName: initialState?.tableName ?? "",
				sortBy: initialState?.sortByDatabaseTableFieldId ?? initialState?.sortByScreenTableFieldId ?? "",
				sortOrder: initialState?.sortOrder || DEFAULT_SORT_ORDER_OPTIONS[0].value,
				recordScope: initialState?.recordScope ?? "firstRecord",
				filterLogicExpression: initialState?.filterLogicExpression ?? "1",
				conditions: mapInitialConditions()
			});

			if (initialTable) {
				const [source, ...rest] = initialTable.split("-");
				const tableId = rest.join("-");
				await getTableFieldOptions(tableId, source as TableSource);
			}
		} catch (error) {
			console.error("Error fetching tables:", error);
			setLoadError(error instanceof Error ? error.message : "Failed to load tables");
			setFetchedScreenFields([]);
			setFormState({
				filterName: initialState?.filterName ?? "",
				table: resolveInitialTable(),
				tableName: initialState?.tableName ?? "",
				sortBy: initialState?.sortByDatabaseTableFieldId ?? initialState?.sortByScreenTableFieldId ?? "",
				sortOrder: initialState?.sortOrder || DEFAULT_SORT_ORDER_OPTIONS[0].value,
				recordScope: initialState?.recordScope ?? "firstRecord",
				filterLogicExpression: initialState?.filterLogicExpression ?? "1",
				conditions: mapInitialConditions()
			});
		} finally {
			setIsLoadingTables(false);
		}
	};

	useEffect(() => {
		if (!open || !platformId) return;

		fetchTables();
	}, [open, platformId, ruleDatabaseMultiRowVariables]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(formState.filterName),
		[getDuplicateNameError, formState.filterName],
	);

	const isSaveDisabled =
		!formState.filterName.trim() ||
		Boolean(duplicateNameError) ||
		!formState.table ||
		!formState.sortBy ||
		!formState.sortOrder ||
		!formState.filterLogicExpression.trim() ||
		isLoadingTables ||
		formState.conditions.some(
			(condition) =>
				(formState.table && !condition.field.trim()) ||
				!condition.operator ||
				!condition.value.trim()
		);

	const updateCondition = (id: string, updates: Partial<Omit<FilterCondition, "id">>) => {
		setFormState((previous) => ({
			...previous,
			conditions: previous.conditions.map((condition) =>
				condition.id === id ? { ...condition, ...updates } : condition
			)
		}));
	};

	const handleAddCondition = () => {
		setFormState((previous) => ({
			...previous,
			conditions: [...previous.conditions, buildCondition()]
		}));
	};

	const handleRemoveCondition = (id: string) => {
		setFormState((previous) => {
			if (previous.conditions.length === 1) {
				return previous;
			}
			return {
				...previous,
				conditions: previous.conditions.filter((condition) => condition.id !== id)
			};
		});
	};

	const handleTableChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const value = event.target.value as string;
		const [source, ...rest] = value.split("-");
		const tableId = rest.join("-");
		const selectedOption = tableVariableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === value);

		setFormState((previous) => ({
			...previous,
			table: value,
			tableName: selectedOption?.label ?? "",
			sortBy: "",
			sortOrder: DEFAULT_SORT_ORDER_OPTIONS[0].value,
		}));
		getTableFieldOptions(tableId, source as TableSource);
	};

	const renderTableOptions = () => {
		if (isLoadingTables) {
			return (
				<MenuItem disabled>
					<CircularProgress size={16} sx={{ mr: 1 }} />Loading...
				</MenuItem>
			);
		}
		if (loadError) {
			return <MenuItem disabled sx={{ color: "error.main" }}>Error loading tables</MenuItem>;
		}
		if (!tableVariableGroups.length) {
			return <MenuItem disabled sx={{ fontStyle: "italic" }}>No options found</MenuItem>;
		}
		return tableVariableGroups.flatMap((group, groupIndex) => [
			<ListSubheader key={`header-${groupIndex}`} sx={{ fontWeight: "bold", color: "text.primary" }}>
				{group.label}
			</ListSubheader>,
			...(group.options ?? []).map((option) => (
				<MenuItem key={`${group.source}-${option.value}`} value={`${group.source}-${option.value}`}>
					{option.label}
				</MenuItem>
			))
		]);
	};

	const getDataTypeForConditionField = (fieldId: string): string | undefined =>
		filterFieldOptions.find((o) => o.value === fieldId)?.type;

	const handleSave = () => {
		const [source, ...rest] = formState.table.split("-");
		const tableId = rest.join("-");
		const selectedOption = tableVariableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === formState.table);

		const isScreen = source === "screen";

		const backingDatabaseTableId =
			source === "databaseVariable"
				? ruleDatabaseMultiRowVariables.find((v) => v.id === tableId)?.source
				: undefined;

		onSave({
			filterName: formState.filterName.trim(),
			databaseTableId: source === "database" ? tableId : backingDatabaseTableId,
			screenTableId: isScreen ? tableId : undefined,
			databaseTableVariableId: source === "databaseVariable" ? tableId : undefined,
			tableName: selectedOption?.label ?? formState.tableName,
			sortByDatabaseTableFieldId: !isScreen && formState.sortBy ? formState.sortBy : undefined,
			sortByScreenTableFieldId: isScreen && formState.sortBy ? formState.sortBy : undefined,
			sortOrder: formState.sortOrder,
			recordScope: formState.recordScope,
			filterLogicExpression: formState.filterLogicExpression.trim(),
			conditions: formState.conditions.map((condition) => {
				const fieldOption = filterFieldOptions.find((o) => o.value === condition.field);
				const trimmed = condition.value.trim();
				const entity = isDecisionValueEntityRow(condition);
				const resolvedField = fieldOption?.name ?? condition.field.trim();
				const resolvedFieldId = condition.field.trim();

				if (!entity) {
					return {
						id: condition.id,
						field: resolvedField,
						databaseTableFieldId: !isScreen ? resolvedFieldId : undefined,
						screenTableFieldId: isScreen ? resolvedFieldId : undefined,
						operator: condition.operator,
						value: trimmed,
						plainTextValue: trimmed,
					};
				}

				return {
					id: condition.id,
					field: resolvedField,
					databaseTableFieldId: !isScreen ? resolvedFieldId : undefined,
					screenTableFieldId: isScreen ? resolvedFieldId : undefined,
					operator: condition.operator,
					value: trimmed,
					plainTextValue: undefined,
					valueDatabaseTableFieldId: condition.valueDatabaseTableFieldId,
					valueScreenFieldId: condition.valueScreenFieldId,
					valueDatabaseTableFieldName: condition.valueDatabaseTableFieldName,
					valueScreenFieldName: condition.valueScreenFieldName,
					valueSimpleVariableName: condition.valueSimpleVariableName,
					valueSimpleVariableId: condition.valueSimpleVariableId,
					valueFormulaVariableId: condition.valueFormulaVariableId,
					valueFormulaVariableName: condition.valueFormulaVariableName,
					valueDatabaseSingleRowVariableId: condition.valueDatabaseSingleRowVariableId,
					valueDatabaseSingleRowVariableName: condition.valueDatabaseSingleRowVariableName,
					valueGlobalConstant: condition.valueGlobalConstant,
				};
			})
		});
	};

	const renderConditionRows = () => (
		<Stack spacing={2}>
			{formState.conditions.map((condition, index) => {
				return (
					<Fragment key={condition.id}>
						<Stack
							direction="row"
							sx={{ mb: 1, alignItems: "flex-start" }}
						>
							<Box
								sx={{
									minWidth: 24,
									textAlign: "center",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "text.secondary",
									fontWeight: 500,
									paddingTop: 4
								}}
							>
								{index + 1}
							</Box>
							<Box
								sx={{
									alignItems: "center",
									display: "flex",
									borderRadius: 2,
									border: "1px solid",
									borderColor: "divider",
									backgroundColor: "background.paper",
									padding: 2,
									width: "100%"
								}}
							>
								<Stack
									direction="row"
									spacing={2}
									sx={{ alignItems: "center", justifyContent: "flex-start", width: "100%" }}
								>
									{/* Field is disabled and unpopulated until Table Variable is selected */}
									<CanvasFormTextField
										select
										fullWidth
										label="Field"
										value={formState.table ? condition.field : ""}
										onChange={(event) => {
											const nextField = event.target.value;
											updateCondition(condition.id, {
												field: nextField,
												value: "",
												plainTextValue: undefined,
												valueDatabaseTableFieldId: undefined,
												valueScreenFieldId: undefined,
												valueDatabaseTableFieldName: undefined,
												valueScreenFieldName: undefined,
												valueSimpleVariableName: undefined,
												valueSimpleVariableId: undefined,
												valueFormulaVariableId: undefined,
												valueFormulaVariableName: undefined,
												valueFormulaVariable: undefined,
												valueDatabaseSingleRowVariableId: undefined,
												valueDatabaseSingleRowVariableName: undefined,
												valueGlobalConstant: undefined,
											});
										}}
										disabled={!formState.table}
										width="100%"
									>
										{formState.table
											? filterFieldOptions.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.name}
												</MenuItem>
											))
											: []}
									</CanvasFormTextField>
									<ComparativeOperatorsSelect
										value={condition.operator}
										onChange={(value) => updateCondition(condition.id, { operator: value })}
										options={comparativeOperatorSelectOptions}
										suppressAutoFetch={suppressComparativeOperatorsAutoFetch}
										disabled={!formState.table}
									/>
									<FormEntitySelect
										label="Value"
										isValueField
										value={getAssignmentValueFormEntityBinding(condition)}
										valueCategory={inferValueCategoryFromEntityFields(condition)}
										onChange={(val: string, category: ResourceCategoryV2, label: string, dataType?: string) => {
											const emptyEntity: Partial<Omit<FilterCondition, "id">> = {
												valueDatabaseTableFieldId: undefined,
												valueScreenFieldId: undefined,
												valueDatabaseTableFieldName: undefined,
												valueScreenFieldName: undefined,
												valueScreenField: undefined,
												valueScreenTableField: undefined,
												valueSimpleVariableName: undefined,
												valueSimpleVariableId: undefined,
												valueFormulaVariableId: undefined,
												valueFormulaVariableName: undefined,
												valueFormulaVariable: undefined,
												valueDatabaseSingleRowVariableId: undefined,
												valueDatabaseSingleRowVariableName: undefined,
												valueGlobalConstant: undefined,
												valueGlobalConstantName: undefined,
											};
											const trimmed = val.trim();
											if (!trimmed || category === "literal") {
												updateCondition(condition.id, {
													...emptyEntity,
													value: trimmed,
													plainTextValue: trimmed || undefined,
												});
												return;
											}
											if (
												category === "databaseTableField" ||
											category === "singleRowDbVariableField" ||
											category === "multiRowDbVariableField"
											) {
												updateCondition(condition.id, {
													...emptyEntity,
													value: val,
													plainTextValue: undefined,
													valueDatabaseTableFieldId: val,
													valueDatabaseTableFieldName: label,
												});
												return;
											}
											if (category === "screenTableField" || category === "screenField") {
												const screenRef = buildScreenFieldRef(label, dataType);
												updateCondition(condition.id, {
													...emptyEntity,
													value: val,
													plainTextValue: undefined,
													valueScreenFieldId: val,
													valueScreenFieldName: label,
													valueScreenField: screenRef,
													valueScreenTableField: screenRef,
												});
												return;
											}
											if (category === "simpleVariable") {
												const id = ruleSimpleVariables.find((v) => v.name === val)?.id;
												updateCondition(condition.id, {
													...emptyEntity,
													value: val,
													plainTextValue: undefined,
													valueSimpleVariableId: id,
													valueSimpleVariableName: val,
												});
												return;
											}
											if (category === "formulaVariable") {
												const id = ruleFormulaVariables.find((v) => v.name === val)?.id;
												updateCondition(condition.id, {
													...emptyEntity,
													value: val,
													plainTextValue: undefined,
													valueFormulaVariableId: id,
													valueFormulaVariableName: val,
													valueFormulaVariable: buildFormulaVariableRef(
														val,
														dataType,
														condition.valueFormulaVariable,
														id,
													),
												});
												return;
											}
											if (category === "databaseSingleRowVariable") {
												const id = ruleDatabaseSingleRowVariables.find((v) => v.name === val)?.id;
												updateCondition(condition.id, {
													...emptyEntity,
													value: val,
													plainTextValue: undefined,
													valueDatabaseSingleRowVariableId: id,
													valueDatabaseSingleRowVariableName: val,
												});
												return;
											}
											if (category === "globalConstant") {
												updateCondition(condition.id, {
													...emptyEntity,
													value: val,
													plainTextValue: undefined,
													valueGlobalConstant: val,
													valueGlobalConstantName: label,
												});
											}
										}}
										visibleGroups={valueFieldVisibleGroups}
										variableGroups={resourceVariableGroups}
										formulaVariables={formulaVariableOptions}
										databaseSingleRowVariableOptions={singleRowDatabaseVariableOptions}
										screenFields={mergedScreenFields}
										globalConstants={globalConstantOptions}
										selectedFieldMeta={
											condition.valueScreenFieldId
												? selectedFieldMetaFromScreenField(
														condition.valueScreenTableField ?? condition.valueScreenField,
														condition.valueScreenFieldId,
													)
												: condition.valueFormulaVariableId || condition.valueFormulaVariableName
													? selectedFieldMetaFromFormulaVariable(
															condition.valueFormulaVariable ??
																buildFormulaVariableRef(
																	condition.valueFormulaVariableName,
																	undefined,
																	undefined,
																	condition.valueFormulaVariableId,
																),
														)
													: undefined
										}
									/>
									<RaisedTrashIconButton
										onClick={() => handleRemoveCondition(condition.id)}
										disabled={formState.conditions.length === 1}
										aria-label={`Remove condition ${index + 1}`}
									/>
								</Stack>
							</Box>
						</Stack>
					</Fragment>
				);
			})}
		</Stack>
	);

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Filter"
			icon={<FilterAlt />}
			iconColor="#F35A35"
			gradient="linear-gradient(135deg, #FF7A3C 0%, #EE3A2D 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Filter"
			width={520}
		>
			{loadError && (
				<Typography color="error" sx={{ mb: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
					{loadError}
				</Typography>
			)}
			<Stack spacing={3} sx={{ p: 2 }} direction="row">
				<CanvasFormTextField
					label="Filter Name"
					value={formState.filterName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							filterName: event.target.value
						}))
					}
					required
					disabled={isLoadingTables}
					width="50%"
				/>
				<CanvasFormTextField
					label="Table Variable"
					select
					value={!tableVariableGroups.length ? "" : formState.table}
					onChange={handleTableChange}
					disabled={!tableVariableGroups.length}
					width="50%"
				>
					{renderTableOptions()}
				</CanvasFormTextField>
			</Stack>
			<Stack spacing={3} sx={{ p: 2 }} direction="row">
				<CanvasFormTextField
					fullWidth
					label="Sort By"
					select
					value={formState.sortBy}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							sortBy: event.target.value
						}))
					}
					disabled={!formState.table}
					width="100%"
				>
					{filterFieldOptions.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.name}
						</MenuItem>
					))}
				</CanvasFormTextField>
				<CanvasFormTextField
					fullWidth
					label="Sort Order"
					select
					value={sortOrderValue}
					disabled={!formState.table}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							sortOrder: event.target.value
						}))
					}
					width="100%"
				>
					{DEFAULT_SORT_ORDER_OPTIONS.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</CanvasFormTextField>
			</Stack>

			<Box sx={{ p: 2 }}>
				<RadioGroup
					row
					value={formState.recordScope}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							recordScope: event.target.value as FilterFormState["recordScope"]
						}))
					}
				>
					<FormControlLabel value="firstRecord" control={<Radio />} label="Only First Record" />
					<FormControlLabel value="allRecords" control={<Radio />} label="All Records" />
				</RadioGroup>
			</Box>

			<Box sx={{ p: 2 }}>
				<LogicField
					label="Filter Logic"
					value={formState.filterLogicExpression}
					onChange={(value) =>
						setFormState((previous) => ({
							...previous,
							filterLogicExpression: value
						}))
					}
					conditionCount={formState.conditions.length}
				/>
			</Box>

			<Box sx={{ p: 2 }}>
				{renderConditionRows()}
				<Box sx={{ textAlign: "center", marginTop: 2 }}>
					<AddConditionButton label="Add Condition" onClick={handleAddCondition} />
				</Box>
			</Box>
		</BaseForm>
	);
};

export default FilterForm;


